import type { Theme } from '@mui/material';
import { getEntityById, getLabelFromId } from '@core/utils/collection';
import { adjustUntilMinContrast } from '@core/utils/color';
import { idToForm, toFormValue } from '@core/utils/form';
import type { Color } from '../../../api/color';
import type { Customer } from '../../../api/customer';
import type { Fabric } from '../../../api/fabric';
import type { MaterialColor } from '../../../api/materialColor';
import type { OrderItem } from '../../../api/order';
import type { Size } from '../../../api/size';
import type { Vestment } from '../../../api/vestment';
import { SizeMode } from '../../enum/sizeMode';
import { VestmentCategory } from '../../enum/vestmentCategory';
import { type VestmentType, getCategory } from '../../enum/vestmentType';
import { getChasubleMaterialField } from '../chasuble';
import { getFullName } from '../customer';
import {
  CUSTOMER_SIZE_ID,
  CUSTOM_SIZE_ID,
  SIZE_FIELD_ABBREVIATIONS,
  type SizeMeasurementField,
} from '../size';
import { hasMeasurementOverrides, resolveMeasurement } from './defaults';
import type {
  AlbItemFormValues,
  BaseLineItemFormValues,
  ChasubleItemFormValues,
  MitreItemFormValues,
  OrderItemFormValues,
  OrderItemSummaryContext,
  OrderItemSummarySegment,
  StoleItemFormValues,
} from './types';

// TODO: Rename to EMPTY_BASE_VALUES, to match chasubleValuesFromApi.
export const EMPTY_LINE_ITEM_BASE: Omit<BaseLineItemFormValues, 'id'> = {
  vestment: '',
  design: null,
  designCode: '',
  quantity: '1',
  size: '',
  forCustomer: '',
  notes: '',
};

// TODO: Rename to baseValuesFromApi, to match chasubleValuesFromApi.
export function baseFieldsFromApi(item: OrderItem): BaseLineItemFormValues {
  const size =
    item.sizeMode === SizeMode.Customer
      ? CUSTOMER_SIZE_ID
      : item.sizeMode === SizeMode.Custom
        ? CUSTOM_SIZE_ID
        : idToForm(item.size);
  return {
    id: item.id,
    vestment: idToForm(item.vestment),
    design: item.design ?? null,
    designCode: item.design?.code ?? '',
    quantity: toFormValue(item.quantity),
    size,
    forCustomer: idToForm(item.forCustomer),
    notes: item.notes ?? '',
  };
}

export function baseLineItemPayload(item: BaseLineItemFormValues) {
  return {
    id: item.id || undefined,
    vestment: Number(item.vestment),
    design: item.design?.id ?? (item.designCode.trim() || undefined),
    quantity: Math.max(1, Number(item.quantity) || 1),
    size: Number(item.size) > 0 ? Number(item.size) : undefined,
    forCustomer:
      item.size === CUSTOMER_SIZE_ID ? Number(item.forCustomer) : undefined,
    notes: item.notes.trim() || undefined,
  };
}

type OrderItemSizeMeasurements = Pick<
  ChasubleItemFormValues &
    AlbItemFormValues &
    StoleItemFormValues &
    MitreItemFormValues,
  SizeMeasurementField
>;

export type OrderItemSizeSummaryParams = {
  sizeName?: string | null;
  size?: Size | null;
  customer?: Customer | null;
  vestmentType?: VestmentType | null;
  vestmentCategory?: VestmentCategory | null;
} & Partial<OrderItemSizeMeasurements>;

export function formatOrderItemSizeSummary(
  fields: readonly SizeMeasurementField[],
  params: OrderItemSizeSummaryParams
): string {
  return formatOrderItemSizeParts(fields, params).join(' / ');
}

export function formatOrderItemSizeParts(
  fields: readonly SizeMeasurementField[],
  {
    sizeName,
    size,
    customer,
    vestmentType,
    vestmentCategory,
    ...measurements
  }: OrderItemSizeSummaryParams
): string[] {
  const hasAnyFilled = fields.some((field) => measurements[field]?.trim());
  const base =
    (customer ? getFullName(customer) : null) ||
    sizeName ||
    (hasAnyFilled ? 'Custom' : null);
  const hasOverrides = hasMeasurementOverrides(fields, measurements, {
    size,
    customer,
    vestmentType,
    vestmentCategory,
  });
  const baseLabel =
    base && hasOverrides && base !== 'Custom' ? `${base} (adjusted)` : base;
  const parts = fields.flatMap((field) => {
    const value = resolveMeasurement(field, {
      formValue: measurements[field],
      customer,
      size,
      vestmentType,
      vestmentCategory,
    });
    return isFilledSummaryPart(value)
      ? [`${SIZE_FIELD_ABBREVIATIONS[field]} ${value}`]
      : [];
  });
  return [baseLabel, ...parts].filter(isFilledSummaryPart);
}

export function isFilledSummaryPart(
  value: string | null | undefined
): value is string {
  const trimmed = value?.trim();
  return !!trimmed && trimmed !== '-';
}

export function segment(
  label: string,
  ...parts: Array<string | null | undefined>
): OrderItemSummarySegment | null {
  const filled = parts.filter(isFilledSummaryPart);
  return filled.length > 0 ? { label, parts: filled } : null;
}

export function segments(
  ...items: Array<OrderItemSummarySegment | null | undefined>
): OrderItemSummarySegment[] {
  return items.filter((item): item is OrderItemSummarySegment => item != null);
}

export function measurementPart(
  prefix: string,
  value: string | null | undefined
): string | null {
  return isFilledSummaryPart(value) ? `${prefix} ${value}` : null;
}

export function withSegmentColor(
  summary: OrderItemSummarySegment | null,
  colors: Color[],
  colorId: string
): OrderItemSummarySegment | null {
  return withSegmentHex(summary, getEntityById(colors, colorId)?.hexCode);
}

export function withSegmentHex(
  summary: OrderItemSummarySegment | null,
  hexCode: string | null | undefined
): OrderItemSummarySegment | null {
  if (!summary) {
    return null;
  }
  const colorHex = hexCode?.trim() || undefined;
  return colorHex ? { ...summary, colorHex } : summary;
}

export function getSummarySegmentTextColor(
  theme: Theme,
  colorHex?: string
): string | undefined {
  if (!colorHex) {
    return undefined;
  }
  try {
    return adjustUntilMinContrast(colorHex, theme.palette.background.paper, 3);
  } catch {
    return undefined;
  }
}

export function fabricSegment(
  label: string,
  fabrics: Fabric[],
  colors: Color[],
  fabricId: string,
  colorId: string
): OrderItemSummarySegment | null {
  const summary = segment(
    label,
    getLabelFromId(fabrics, fabricId),
    getLabelFromId(colors, colorId)
  );
  return withSegmentColor(summary, colors, colorId);
}

export function getVestmentsByCategory(
  vestments: Vestment[],
  category: VestmentCategory | undefined
): Vestment[] {
  if (!category) {
    return [];
  }
  return vestments.filter(
    (vestment) => getCategory(vestment.type) === category
  );
}

export function formatMaterialLabel(
  option: Pick<MaterialColor, 'typeName' | 'name'>
): string {
  return option.typeName ? `${option.typeName} ${option.name}` : option.name;
}

export function getMaterialSummary(
  collection: MaterialColor[],
  idStr: string
): string | null {
  const found = getEntityById(collection, idStr);
  return found ? formatMaterialLabel(found) : null;
}

export function materialSegment(
  label: string,
  collection: MaterialColor[],
  idStr: string
): OrderItemSummarySegment | null {
  const material = getEntityById(collection, idStr);
  return withSegmentHex(
    segment(label, material ? formatMaterialLabel(material) : null),
    material?.hexCode
  );
}

type ChasubleMaterialValues = Pick<
  ChasubleItemFormValues,
  'patchingType' | 'gothicType' | 'thread' | 'trimming' | 'galoon'
>;

type ChasubleMaterialContext = Pick<
  OrderItemSummaryContext,
  'threads' | 'trimmings' | 'galoons'
>;

export function getChasubleMaterial(
  vestmentType: VestmentType | null | undefined,
  item: ChasubleMaterialValues,
  ctx: ChasubleMaterialContext
): MaterialColor | undefined {
  switch (getChasubleMaterialField(vestmentType, item)) {
    case 'thread':
      return getEntityById(ctx.threads, item.thread);
    case 'trimming':
      return getEntityById(ctx.trimmings, item.trimming);
    case 'galoon':
      return getEntityById(ctx.galoons, item.galoon);
    default:
      return undefined;
  }
}

export function getChasubleMaterialSummary(
  vestmentType: VestmentType | null | undefined,
  item: ChasubleMaterialValues,
  ctx: ChasubleMaterialContext
): string | null {
  const material = getChasubleMaterial(vestmentType, item, ctx);
  return material ? formatMaterialLabel(material) : null;
}

export function getFormValuesCategory(
  item: OrderItemFormValues
): VestmentCategory | undefined {
  if ('collarFabric' in item) {
    return VestmentCategory.Chasuble;
  }
  if ('shoulder' in item) {
    return VestmentCategory.Alb;
  }
  if ('cord' in item) {
    return VestmentCategory.Stole;
  }
  if ('liningFabric' in item) {
    return VestmentCategory.Mitre;
  }
  return undefined;
}

/**
 * Trims the design code and drops the design when it no longer matches the
 * code, so a free-typed code is resolved (or created) by the server.
 */
export function syncDesignWithCode<T extends BaseLineItemFormValues>(
  item: T
): T {
  const designCode = item.designCode.trim();
  return {
    ...item,
    designCode,
    design: item.design?.code === designCode ? item.design : null,
  };
}
